/*
 * Demo content for the SVG app mockups (components/app/AppPhone.js), customized per landing page.
 * Card subtitles follow the app's formats in lowkeyclient utils/helpers/RoutineHelpers.js. Names, emails, and phone
 * numbers are fictional (555 numbers).
 */
import { PROFESSIONS } from './professions';

// IANA zones for city pages so routine times show the local abbreviation (PDT, CST, ...).
const CITY_ZONES = {
  'America/Vancouver': [
    'vancouver',
    'surrey',
    'victoria',
    'seattle',
    'portland',
    'los-angeles',
    'san-diego',
    'san-francisco',
    'san-jose',
    'oakland',
    'orange-county',
  ],
  'America/Edmonton': ['calgary', 'edmonton'],
  'America/Regina': ['regina', 'saskatoon'],
  'America/Winnipeg': ['winnipeg'],
  'America/Chicago': ['chicago', 'dallas', 'austin', 'san-antonio'],
  'America/Toronto': [
    'toronto',
    'kitchener',
    'ottawa',
    'london',
    'montreal',
    'nyc',
    'boston',
    'jersey-city',
    'hartford',
    'new-haven',
    'detroit',
    'washington-dc',
    'atlanta',
    'tampa',
    'miami',
    'orlando',
  ],
};

// Zone abbreviation like the app's moment.tz(...).zoneAbbr(). Defaults to the visitor's own zone in North America.
const zoneAbbr = (channel) => {
  const zone = Object.keys(CITY_ZONES).find((z) => CITY_ZONES[z].includes(channel));
  const abbr = (timeZone) => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(new Date());
      return parts.find((p) => p.type === 'timeZoneName')?.value;
    } catch (e) {
      return undefined;
    }
  };

  const local = abbr(zone);
  return local && /^[A-Z]{3,4}$/.test(local) ? local : abbr('America/New_York') || 'EST';
};

const timer = (title, minutes) => ({
  title,
  icon: 'lock_gray',
  subtitle: `Tap to unlock for ${
    minutes >= 60 ? `${minutes / 60} hour${minutes > 60 ? 's' : ''}` : `${minutes} minutes`
  }`,
});
const timerOn = (title, left, percentage) => ({
  title,
  icon: 'unlock_green',
  active: true,
  percentage,
  subtitle: `${left} minutes remaining`,
});
const passcode = (title, code) => ({ title, icon: 'key_gray', subtitle: `Tap to activate passcode - ${code}` });
const passcodeOn = (title, code) => ({
  title,
  icon: 'key_green',
  active: true,
  subtitle: `Passcode "${code}" is active`,
});
const routine = (tz, title, days, from, to) => ({
  title,
  icon: 'clock_gray',
  subtitle: `Tap to activate - ${days}\n${from} to ${to} ${tz}`,
});
const routineOn = (tz, title, days, from, to) => ({
  title,
  icon: 'clock_green',
  active: true,
  subtitle: `Active ${days}\n${from} to ${to} ${tz}`,
});

// Body of the buzz-in push for the page's lead routine, wrapped onto two lines.
const granted = (title) => ['🚪 Access granted with', `“${title}”`];

// Emoji per profession page, for the client routine name.
const PROFESSION_ROUTINES = {
  law_office: 'Client Meetings ⚖️',
  tattoo_studio: 'Tattoo Clients 🎨',
  massage: 'Massage Clients 💆',
  therapists: 'Therapy Clients 🛋️',
  counselors: 'Counseling 🛋️',
  accountants: 'Tax Clients 🧾',
  tutors: 'Students 📚',
  photographers: 'Photo Shoots 📸',
  estheticians: 'Facial Clients ✨',
};

const pages = (tz) => {
  const everyday = {
    cards: [
      timerOn('Food Delivery 🍕', 47, 78),
      routineOn(tz, 'Dog Walker 🐕', 'weekdays', '12:00 PM', '1:00 PM'),
      timer('Expecting Guests 👋', 30),
      passcode('Guest Passcode 🔢', '5555'),
      routine(tz, 'Weekly Cleaner 🧽', 'Saturdays', '5:00 PM', '7:00 PM'),
    ],
  };
  const foodApp = (name, emoji) => ({
    cards: [
      timerOn(`${name} ${emoji}`, 47, 78),
      routineOn(tz, 'Lunch Delivery 🥗', 'weekdays', '12:00 PM', '1:00 PM'),
      timer('Expecting Guests 👋', 30),
      passcode('Guest Passcode 🔢', '5555'),
    ],
  });
  const property = {
    building: 'Maple Court',
    cards: [
      passcodeOn('Residents 🔢', '2580'),
      routineOn(tz, 'Cleaning Crew 🧽', 'weekdays', '9:00 AM', '11:00 AM'),
      passcode('Maintenance 🔧', '4417'),
      timer('Package Delivery 📦', 60),
      routine(tz, 'Open House 🏠', 'Saturdays', '1:00 PM', '4:00 PM'),
    ],
  };

  return {
    default: everyday,
    doordash: foodApp('DoorDash', '🍔'),
    ubereats: foodApp('UberEats', '🍔'),
    instacart: {
      cards: [
        timerOn('Instacart 🥕', 52, 86),
        routineOn(tz, 'Grocery Run 🛒', 'Tuesdays', '3:00 PM', '6:00 PM'),
        timer('Expecting Guests 👋', 30),
        passcode('Guest Passcode 🔢', '5555'),
      ],
    },
    amazon_packages: {
      cards: [
        routineOn(tz, 'Amazon Packages 📦', 'weekdays', '9:00 AM', '5:00 PM'),
        timer('Courier Pickup 🚚', 30),
        passcode('Guest Passcode 🔢', '5555'),
        routine(tz, 'Weekly Cleaner 🧽', 'Saturdays', '5:00 PM', '7:00 PM'),
      ],
    },
    groceries: {
      cards: [
        timerOn('Grocery Delivery 🛒', 52, 86),
        routine(tz, 'Weekly Groceries 🥕', 'Sundays', '10:00 AM', '1:00 PM'),
        timer('Expecting Guests 👋', 30),
        passcode('Guest Passcode 🔢', '5555'),
      ],
    },
    delivery: {
      cards: [
        routineOn(tz, 'Deliveries 📦', 'weekdays', '9:00 AM', '6:00 PM'),
        timer('Food Delivery 🍕', 60),
        passcode('Courier Passcode 🔢', '3141'),
        timer('Expecting Guests 👋', 30),
      ],
    },
    roommates: {
      ...everyday,
      users: [
        { email: 'sam.rivera@icloud.com', phone: '(416) 555-0148', color: 1 },
        { email: 'maya.chen@gmail.com', phone: '(416) 555-0193', color: 2 },
        { email: 'jordan.lee@gmail.com', phone: '(647) 555-0122', color: 4 },
      ],
    },
    caregivers: {
      cards: [
        routineOn(tz, 'Caregiver 🩺', 'every day', '8:00 AM', '8:00 PM'),
        passcode('Family Passcode 🔢', '1950'),
        timer('Pharmacy Delivery 💊', 30),
        routine(tz, 'Physio Visit 🦵', 'Thursdays', '2:00 PM', '3:00 PM'),
      ],
    },
    assisted_living: {
      cards: [
        routineOn(tz, 'Visiting Hours 👵', 'every day', '10:00 AM', '8:00 PM'),
        passcode('Care Staff 🩺', '7310'),
        timer('Pharmacy Delivery 💊', 30),
        passcode('Family Passcode 🔢', '1950'),
      ],
    },
    rover: {
      cards: [
        routineOn(tz, 'Dog Walker 🐕', 'weekdays', '12:00 PM', '1:00 PM'),
        passcode('Dog Sitter 🦴', '2468'),
        timer('Food Delivery 🍕', 60),
        timer('Expecting Guests 👋', 30),
      ],
    },
    pet_sitting: {
      cards: [
        routineOn(tz, 'Pet Sitter 🐈', 'every day', '8:00 AM', '9:00 AM'),
        passcode('Dog Walker 🐕', '2468'),
        routine(tz, 'Groomer Visit ✂️', 'Saturdays', '10:00 AM', '11:00 AM'),
        timer('Food Delivery 🍕', 60),
      ],
    },
    airbnb: {
      cards: [
        passcodeOn('Airbnb Guests 🏠', '8824'),
        routine(tz, 'Turnover Clean 🧹', 'Sundays', '11:00 AM', '3:00 PM'),
        passcode('Maintenance 🔧', '4417'),
        timer('Expecting Guests 👋', 30),
      ],
    },
    short_term_rentals: {
      cards: [
        passcodeOn('Guest Check-In 🧳', '8824'),
        routineOn(tz, 'Turnover Clean 🧹', 'weekends', '11:00 AM', '3:00 PM'),
        passcode('Maintenance 🔧', '4417'),
        timer('Grocery Drop-Off 🛒', 30),
      ],
    },
    property_access_software: property,
    business: property,
    clients: (title) => ({
      cards: [
        routineOn(tz, title, 'weekdays', '9:00 AM', '5:00 PM'),
        passcode('Walk-Ins 🔢', '1234'),
        timer('Courier 📦', 30),
        routine(tz, 'Saturday Hours 📅', 'Saturdays', '10:00 AM', '2:00 PM'),
      ],
    }),
  };
};

// The mockup content for a landing page: { cards, building?, users?, notification }.
export const appScreensFor = (channel) => {
  const all = pages(zoneAbbr(channel));
  let page = all[channel] || all.default;
  if (channel === 'buzz_in_clients') {
    page = all.clients('Clients 💼');
  } else if (PROFESSIONS[channel]) {
    page = all.clients(PROFESSION_ROUTINES[channel] || 'Clients 💼');
  }

  return { ...page, notification: granted(page.cards[0].title) };
};

// Other buildings in the Business page's building switcher, current building first.
export const BUILDINGS = [
  { name: 'Maple Court', phone: '(604) 555-0131' },
  { name: 'Harbourview Lofts', phone: '(604) 555-0178', primary: true },
  { name: 'Elm Street Apartments', phone: '(206) 555-0142' },
  { name: 'Riverside Condos', phone: '(503) 555-0116' },
  { name: 'King St Towers', phone: '(416) 555-0167' },
];
